<i18n lang="yaml">
  pt:
    fallback-text: "Conteúdo vazio. Clique em 'Adicionar' para criar um novo conteúdo."
</i18n>

<template>
  <div
    ref="element"
    class="element p-relative overflow-hidden"
  >
    <div
      class="element__wrapper"
      :class="{
        'element__wrapper--headless': headless,
        'element__wrapper--collapsed': isCollapsed
      }"
    >
      <template v-if="!headless">
        <div
          data-grid="nowrap"
          class="element__header grid--va-center"
        >
          <div
            data-grid
            class="element__icon grid--center grid--va-center"
          >
            <CheckIcon class="w-75 h-75" />
          </div>

          <div
            class="element__text"
            :class="{ 'pe-2': !$hasSlot('action-button') }"
          >
            <h3 class="element__title m-0">
              {{ title }}
            </h3>

            <p
              v-if="description || $hasSlot('description')"
              class="element__description overflow-hidden"
              :class="{ 'pe-3': $hasSlot('action-button') }"
            >
              <slot name="description">
                {{ description }}
              </slot>
            </p>
          </div>

          <slot name="action-button" />

          <template v-if="!untoggleable">
            <div
              class="element__vdivider opacity-5"
              :class="{ 'ms-auto': $hasSlot('action-button') }"
            />

            <div
              data-grid
              class="element__switch cursor-pointer grid--center grid--va-center"
              @click.stop="toggleIsCollapsed"
            >
              <slot name="toggle-button">
                <ArrowDownIcon />
              </slot>
            </div>
          </template>
        </div>

        <div
          v-if="!contentless"
          class="element__hdivider w-100 opacity-5"
        />
      </template>

      <template v-if="!contentless">
        <div
          ref="elementList"
          class="element__items overflow-hidden"
          :class="{ 'element__items--collapsed': isCollapsed }"
        >
          <slot
            :collapsed="isCollapsed"
            name="collapsable-items"
          >
            <div class="element__empty text-center py-2">
              {{ $t('fallback-text') }}
            </div>
          </slot>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

import CheckIcon from '../../icons/CheckIcon'
import ArrowDownIcon from '../../icons/ArrowDownIcon'

export default defineComponent({
  name: 'ContentCollapsable',

  components: {
    CheckIcon,
    ArrowDownIcon
  },

  provide () {
    return {
      title: this.localTitle
    }
  },

  props: {
    title: {
      type: String,
      required: true
    },

    description: {
      type: String,
      default: ''
    },

    unfolded: {
      type: Boolean,
      default: false
    },

    untoggleable: {
      type: Boolean,
      default: false
    },

    headless: {
      type: Boolean,
      default: false
    },

    contentless: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isCollapsed: true
    }
  },

  computed: {
    localTitle () {
      const { title } = this

      return title
    },

    listSlots () {
      return Object.keys(this.$slots)
    }
  },

  mounted () {
    if (!this.unfolded) return

    setTimeout(() => {
      this.isCollapsed = false
    }, 200)
  },

  methods: {
    toggleIsCollapsed () {
      if (this.listSlots.includes('toggle-button')) return

      this.isCollapsed = !this.isCollapsed
    }
  }
})
</script>

<style lang="scss" scoped>
  .element {
    border-radius: 16px;

    & + & {
      margin: 17px 0 0;
    }

    &__wrapper {
      display: grid;
      grid-template-rows: 148px 1px 1fr;

      transition: all .4s ease-in-out;
      background-color: rgb(var(--white));

      &--headless {
        grid-template-rows: 1fr;
      }

      &--collapsed:not(&--headless) {
        --switch-rotate: 0deg;
        --color: rgb(var(--gray1));
        --shadowColor: rgba(var(--black), 0.08);

        grid-template-rows: 148px 0 0fr;
      }
    }

    &__header {
      height: 148px;
      padding: 41px 48px 33px 53px;
    }

    &__icon {
      width: 37px;
      height: 37px;
      flex: 0 0 37px;

      border-radius: 50%;
      color: rgb(var(--green2));
      background-color: rgba(var(--green2), .1);
    }

    &__text {
      padding-left: 24px;
    }

    &__title {
      color: var(--primary-dark2);
      font: 600 1.8rem "Archivo", sans-serif;

      &:first-letter {
        text-transform: uppercase;
      }
    }

    &__description {
      margin: 8px 0 0;

      letter-spacing: .5px;
      color: rgb(var(--gray13));
      font: 400 1.4rem "Nunito", sans-serif;

      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }

    &__addbutton {
      width: 191px;
      height: 40px;

      line-height: 1.8rem;
      box-shadow: 0 2px 8px rgba(var(--primary-rgb), 0.3);

      svg {
        width: 24px;
        max-width: 24px;
        aspect-ratio: 1;
        margin-inline: 0 16px;
      }
    }

    &__vdivider {
      width: 1px;
      height: 49px;

      margin-inline: auto 32px;
      background-color: rgb(var(--gray9));

      button + & {
        margin-left: 32px;
      }
    }

    &__switch {
      width: 36px;
      height: 36px;
      flex: 0 0 36px;

      border-radius: 50%;
      color: var(--color, rgb(var(--primary-rgb)));
      box-shadow: 0 4px 8px var(--shadowColor, rgba(var(--primary-rgb), 0.2));

      &,
      svg {
        transition: all .4s ease-in-out;
      }

      svg {
        width: 12px;
        fill: var(--color);
        transform: var(--switch-rotate, rotate(180deg));
      }
    }

    &__hdivider {
      background-color: rgb(var(--gray9));
    }

    &__newbutton {
      background-color: transparent;

      color: var(--primary);
      font: 700 1.6rem "Nunito", sans-serif;
    }

    &__items {
      padding: 11px 21px;
      transition: padding .4s ease-in-out;

      &--collapsed {
        padding-block: 0;
      }
    }

    &__empty {
      color: rgb(var(--gray2));
    }
  }
</style>
